import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useLocation } from 'react-router-dom';
import Aside from './aside';
import Header from './header';
import Footer from './footer';
import { FCProps } from 'types/FCProps';
import ImpersonatedNotification from 'components/noticeTopBar/ImpersonatedNotification';
import { SessionContext } from 'contexts/session';
import MigratedTopNotice from 'components/migratedTopNotice/MigratedTopNotice';
import { setMigrationInfo, setNotificationReaded } from 'services/users';
import TeamsPlaylistReportNotice from 'components/TeamsPlaylistReportNotice/TeamsPlaylistReportNotice';
import MaintenanceScheduledBanner from 'components/maintenanceScheduledBanner/MaintenanceScheduledBanner';
import useCustomContentAvailability from 'hooks/useCustomContentAvailability';
import { CustomContentAvailabilityContext } from 'contexts/customContentAvailability';
import { Body, Content, Main, TopBarsStack } from './styles';

const Layout = ({ children }: FCProps): JSX.Element => {
  const { user, isTeamManager, isImpersonated, notShowingDrawers } = useContext(SessionContext);
  const [notificationsReaded, setNotificationsReaded] = useState<number[]>([]);
  const topBarsRef = useRef<HTMLDivElement | null>(null);
  const hasInitializedCustomContentCheckRef = useRef(false);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const location = useLocation();
  const notifications = user.notifications ?? [];
  const customContentAvailability = useCustomContentAvailability();
  const { hasCustomContent, refreshCustomContentAvailability } = customContentAvailability;
  const refreshCustomContentAvailabilityRef = useRef(refreshCustomContentAvailability);

  const isMigrated = user.should_show_migration_info;

  const noticeToShow = useMemo(() => {
    if (isMigrated) {
      return { kind: 'migrated' as const };
    }

    const findNotification = (kind: string) =>
      notifications.find((notification) => notification.kind === kind);

    const canShowTeamsNotice = (kind: 'playlists' | 'reporting') => {
      if (isImpersonated || !isTeamManager) return false;
      const notification = findNotification(kind);
      if (!notification) return false;
      if (kind === 'playlists' && location.pathname !== '/playlists') return false;
      return true;
    };

    if (!isMobile && canShowTeamsNotice('playlists')) {
      return { kind: 'playlists' as const, notificationId: findNotification('playlists')?.id };
    }

    if (!isMobile && canShowTeamsNotice('reporting')) {
      return { kind: 'reporting' as const, notificationId: findNotification('reporting')?.id };
    }

    return null;
  }, [isMigrated, notifications, isImpersonated, isTeamManager, location.pathname, isMobile]);

  useEffect(() => {
    const el = topBarsRef.current;
    if (!el) return;

    const updateHeight = () => {
      setTopBarHeight(el.clientHeight);
    };

    updateHeight();

    const mo = new MutationObserver(() => updateHeight());
    mo.observe(el, { childList: true, subtree: true, attributes: true, characterData: true });
    window.addEventListener('resize', updateHeight);

    return () => {
      mo.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, []);

  useEffect(() => {
    if (!user.migration_info_start_time) {
      setMigrationInfo();
    }
  }, []);

  useEffect(() => {
    refreshCustomContentAvailabilityRef.current = refreshCustomContentAvailability;
  }, [refreshCustomContentAvailability]);

  useEffect(() => {
    // Hook already checks on app load (mount). Re-check on route changes.
    if (!hasInitializedCustomContentCheckRef.current) {
      hasInitializedCustomContentCheckRef.current = true;
      return;
    }

    refreshCustomContentAvailabilityRef.current();
  }, [location.pathname]);

  useEffect(() => {
    if (!noticeToShow?.notificationId) return;
    if (notificationsReaded.includes(noticeToShow.notificationId)) return;
    setNotificationsReaded((prevState) => [...prevState, noticeToShow.notificationId]);
    setNotificationReaded({ notification_id: noticeToShow.notificationId });
  }, [noticeToShow?.notificationId, notificationsReaded]);

  const showNoticeComponent = () => {
    if (!noticeToShow) return null;
    if (noticeToShow.kind === 'migrated') {
      return <MigratedTopNotice />;
    } else if (noticeToShow.kind === 'playlists') {
      return <TeamsPlaylistReportNotice type="playlists" />;
    } else if (noticeToShow.kind === 'reporting') {
      return <TeamsPlaylistReportNotice type="reporting" />;
    }
  };

  return (
    <CustomContentAvailabilityContext.Provider value={customContentAvailability}>
      <Body header topBarHeight={topBarHeight}>
        <TopBarsStack ref={topBarsRef}>
          {showNoticeComponent()}
          <MaintenanceScheduledBanner />
          <ImpersonatedNotification />
        </TopBarsStack>
        <Aside
          translateY={notShowingDrawers && `${topBarHeight}px`}
          hasCustomContent={hasCustomContent}
        />
        <Header translateY={notShowingDrawers && `${topBarHeight}px`} />
        <Main translateY={notShowingDrawers && `${topBarHeight}px`}>
          <Content isMobile={isMobile} top={topBarHeight}>
            {children}
          </Content>
          <Footer />
        </Main>
      </Body>
    </CustomContentAvailabilityContext.Provider>
  );
};

export default Layout;
